import { sanitizeConversationPostContent, sanitizeUserContent } from './sanitize';

/**
 * Utility functions for handling user mentions in content
 */

function escapeHtml(content: string): string {
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Strip trailing sentence punctuation that usually isn’t part of the URL */
function trimTrailingUrlPunctuation(raw: string): { core: string; tail: string } {
  let core = raw;
  let tail = '';
  while (core.length > 0 && /[.!?,;:]$/.test(core)) {
    tail = core.slice(-1) + tail;
    core = core.slice(0, -1);
  }
  return { core, tail };
}

const URL_SPLIT_RE = /(https?:\/\/[^\s<>'"]+)/gi;

/**
 * Renders content with clickable user mentions
 * Converts @username to clickable links
 * SECURITY: Sanitizes content to prevent XSS attacks
 */
export function renderContentWithMentions(content: string): string {
  if (!content) return '';

  const escaped = escapeHtml(content);

  const withMentions = escaped.replace(
    /@(\w+)/g,
    '<a href="/profile/$1" class="text-accent-text hover:text-accent-text/80 font-medium hover:underline transition-colors">@$1</a>'
  );

  return sanitizeUserContent(withMentions);
}

/**
 * Conversation posts/comments: mentions + http(s) URLs as external links (confirmed in UI before opening).
 * URLs are stored in data-external-url; actual navigation is handled by ConversationRichContent.
 */
export function renderConversationContentWithMentions(content: string): string {
  if (!content) return '';

  const escaped = escapeHtml(content);
  const parts = escaped.split(URL_SPLIT_RE);

  const html = parts
    .map((part, index) => {
      if (index % 2 === 0) {
        return part.replace(
          /@(\w+)/g,
          '<a href="/profile/$1" class="text-accent-text hover:text-accent-text/80 font-medium hover:underline transition-colors">@$1</a>'
        );
      }

      const { core, tail } = trimTrailingUrlPunctuation(part);
      if (!/^https?:\/\//i.test(core)) {
        return part;
      }

      const encoded = encodeURIComponent(core);
      const linkClass =
        'text-accent-text hover:text-accent-text/80 font-medium underline decoration-accent-text/40 underline-offset-2 break-all convo-external-link';
      const link = `<a href="#" class="${linkClass}" data-external-url="${encoded}" rel="noopener noreferrer">${core}</a>`;
      return link + tail;
    })
    .join('');

  return sanitizeConversationPostContent(html);
}

/**
 * Extract mentioned usernames from content
 */
export function extractMentions(content: string): string[] {
  const mentionMatches = content.match(/@(\w+)/g);
  if (!mentionMatches) return [];
  
  return mentionMatches.map(match => match.substring(1));
}

/**
 * Get user IDs from mentioned usernames
 */
export async function getMentionedUserIds(usernames: string[]): Promise<string[]> {
  if (usernames.length === 0) return [];
  
  try {
    const { supabase } = await import('../supabase');
    
    const { data: users, error } = await supabase
      .from('users')
      .select('id')
      .in('username', usernames);

    if (error) throw error;
    
    return users?.map(user => user.id) || [];
  } catch (err) {
    console.error('Error fetching mentioned user IDs:', err);
    return [];
  }
}